import { useMemo, useState, type ReactNode } from 'react'
import { Trash2, ExternalLink, Plus, X } from 'lucide-react'
import { toast } from 'sonner'
import {
  EditableGrid,
  cellKey,
  type EditableColumnDef,
} from '@/components/ui/EditableGrid/EditableGrid'
import {
  Button,
  Modal,
  ProductPicker,
} from '@/components/ui'
import type { PickerValue } from '@/components/ui/ProductPicker/ProductPicker'
import { Chip } from '@/components/ui/Chip/Chip'
import { ApiError } from '@/lib/api/jsonFetch'
import { odooUrl } from '@/lib/utils/odoo-url'
import {
  useCreateItem,
  useDeleteItem,
  usePackagings,
  usePatchItem,
} from '../api/queries'
import type { ItemPatch, OdooM2O, PricelistItem, PricelistSummary } from '../api/types'
import { formatShortDate, hasUpcomingCost, upcomingMarginPct } from '../utils/upcoming-cost'

const MODULE_ID = 'customer-pricelists'

/**
 * Sprint 01b Chunk E — the editable surface.
 *
 * Replaces `ItemsReadOnlyTable` in `PricelistDetail` when a pricelist has
 * `compute_price === 'fixed'` items. Non-fixed items (percentage /
 * formula) continue to render read-only below ("Edit in Odoo backoffice")
 * — editing those modes would require a richer Odoo rule-editor that's
 * out of Sprint 01 scope.
 *
 * Mutations are immediate-commit (Sprint 01 D-3: Odoo is the source of
 * truth). Dirty state is transient — a cell marks dirty while its
 * mutation is in flight and clears on success. Odoo ValidationError →
 * HTTP 400 → routed inline via the `cellErrors` prop added to
 * EditableGrid in Sprint 01a Chunk C (D-6). Non-400 errors fall back to
 * a toast.
 *
 * New rows live in `draftRows` local state with a synthetic negative id.
 * A draft POSTs once the minimum required fields are set (product_id +
 * fixed_price). On success the draft is removed; the items refetch
 * surfaces the real row.
 *
 * Optimistic layering: cell commits on existing rows layer through
 * `pendingEdits` so the grid paints the new value immediately (the
 * picker's name, a typed number) without waiting for the refetch. On
 * mutation success the patch is cleared; on error the patch is cleared
 * AND a `cellErrors` entry is set.
 */

interface DraftRow {
  id: number // synthetic negative sentinel — never hits Odoo
  _draft: true
  compute_price: 'fixed'
  applied_on: '1_product'
  product_id: OdooM2O
  fixed_price: number | false
  min_quantity: number
  date_start: string | false
  date_end: string | false
  customer_part_number: string | false
  manufacturer_trade_name: string | false
  manufacturer_id: OdooM2O
  country_of_origin_id: OdooM2O
  packaging_id: OdooM2O
  uom_name?: string | null
  // Fields not editable via the grid but present to satisfy the row shape.
  pricelist_id: OdooM2O
  product_tmpl_id: OdooM2O
  currency_id: OdooM2O
  percent_price?: number | false
  categ_id?: OdooM2O
  manufacturer_name: string | false
  country_of_origin: string | false
  // Decorated fields from server reads; draft rows haven't been written
  // yet, so these stay null / empty.
  last_paid_price: number | null
  last_paid_uom_name: string | null
  last_paid_at: string | null
  last_paid_packaging_name: string | null
  last_paid_packaging_qty: number | null
  approved_vendor_ids: number[]
  approved_vendors: Array<{ id: number; name: string }>
  // Synthetic grid-only fields. Declared on both DraftRow and
  // PricelistItem so `keyof GridRow` (= keys present in BOTH variants
  // of the union) keeps these key names available for column-defs.
  _margin_pct?: number | null
  _increase_pct?: number | null
  _upcoming_cost?: null
}

type GridRow = (PricelistItem & { _draft?: false }) | DraftRow

function isDraft(row: GridRow): row is DraftRow {
  return (row as DraftRow)._draft === true
}

/**
 * Pricing-field changes (`fixed_price`, `min_quantity`) and the Increase
 * %-bulk action hold here before hitting Odoo. The optimistic update has
 * already been applied so the grid renders the would-be value; confirm
 * fires the PATCH, cancel rolls the optimistic back. Used by the confirm
 * dialog.
 */
interface PendingConfirm {
  itemId: number
  rowIdx: number  // for routing 400 errors back to the right cell
  field: 'fixed_price' | 'min_quantity'
  oldValue: number | null
  newValue: number
  rowLabel: string
  /**
   * 'cell' = the operator typed into the price/min-qty cell directly.
   * 'increase' = the operator typed an Increase %; we recomputed
   * fixed_price for them. Drives error-routing back to the right cell
   * AND the secondary line in the dialog body.
   */
  source: 'cell' | 'increase'
  /** Only set when source='increase'. */
  increasePct?: number
}

const PRICE_FIELD_LABELS: Record<'fixed_price' | 'min_quantity', string> = {
  fixed_price: 'Price',
  min_quantity: 'Min qty',
}

/** Extract an id from an Odoo Many2one wire value, treating `false` as
 *  null. The picker UI uses `null`-for-unset; Odoo uses `false`. */
function m2oName(v: OdooM2O | null | undefined): string | undefined {
  return v ? v[1] : undefined
}

function m2oId(v: OdooM2O | null | undefined): number | null {
  if (!v) return null
  return v[0]
}

/** Convert wire `OdooM2O` to the picker's null-for-unset shape. */
function toPickerValue(v: OdooM2O | null | undefined): PickerValue {
  if (!v) return null
  return v
}

export interface PricelistEditorProps {
  pricelist: PricelistSummary
  items: PricelistItem[]
  refetch: () => void
  fetching: boolean
}

export function PricelistEditor({
  pricelist,
  items,
  refetch,
  fetching,
}: PricelistEditorProps) {
  const patchItem = usePatchItem()
  const createItem = useCreateItem()
  const deleteItem = useDeleteItem()

  const [draftRows, setDraftRows] = useState<DraftRow[]>([])
  const [pendingEdits, setPendingEdits] = useState<
    Map<number, Partial<PricelistItem>>
  >(new Map())
  const [cellErrors, setCellErrors] = useState<Map<string, string>>(new Map())

  // Pricing-field edits + the Increase % action both hold here before
  // hitting Odoo so the operator can confirm. Non-pricing fields skip
  // this gate. The optimistic update has already been applied (so the
  // grid shows the would-be value); confirm fires the PATCH, cancel
  // rolls the optimistic back.
  const [pendingConfirm, setPendingConfirm] = useState<PendingConfirm | null>(null)

  const fixedItems = useMemo(
    () => items.filter((it) => it.compute_price === 'fixed'),
    [items],
  )
  const nonFixedItems = useMemo(
    () => items.filter((it) => it.compute_price !== 'fixed'),
    [items],
  )

  // Rows rendered in the grid = server rows with pending edits layered on top,
  // plus any client-only draft rows appended at the bottom.
  const allRows: GridRow[] = useMemo(() => {
    const merged: GridRow[] = fixedItems.map((item) => ({
      ...item,
      ...(pendingEdits.get(item.id) ?? {}),
    }))
    return [...merged, ...draftRows]
  }, [fixedItems, pendingEdits, draftRows])

  // "Only rows with an upcoming cost change" — for setting future-dated
  // prices while a vendor increase is scheduled. Draft rows always stay.
  const [onlyUpcoming, setOnlyUpcoming] = useState(false)
  const upcomingCount = useMemo(
    () => allRows.filter((r) => !isDraft(r) && hasUpcomingCost(r)).length,
    [allRows],
  )
  const rows: GridRow[] = useMemo(
    () => (onlyUpcoming ? allRows.filter((r) => isDraft(r) || hasUpcomingCost(r)) : allRows),
    [allRows, onlyUpcoming],
  )

  // ─── column definitions ──────────────────────────────────────────

  const columns: EditableColumnDef<GridRow>[] = useMemo(
    () => [
      {
        key: 'product_id',
        header: 'Product',
        width: '22%',
        // Existing items lock their product — Odoo is the source of
        // truth for product↔pricelist linkage; changing it in-app
        // would silently break SO history and chatter. Draft rows
        // (still being created) keep the picker so the operator can
        // set the initial product before first commit.
        readonly: (row: GridRow) => !isDraft(row),
        render: (value, row) => {
          const variant = value as OdooM2O | undefined
          const tmpl = !isDraft(row) ? row.product_tmpl_id : undefined
          const variantId = variant && variant[0] ? variant[0] : null
          const tmplId = tmpl && tmpl[0] ? tmpl[0] : null
          const label =
            (variant && variant[1]) || (tmpl && tmpl[1]) || null
          if (!label) {
            // Draft row, no product yet — let the cell click open the
            // ProductPicker via renderEditor (row is not readonly here).
            return <span className="text-tertiary">Pick product…</span>
          }
          // Existing item — make the WHOLE label a clickable link into
          // Odoo. Variant id wins when set; otherwise template. The
          // readonly predicate above prevents the cell click from
          // entering edit mode, so the link is the only interaction.
          const odooHref = variantId
            ? odooUrl('product.product', variantId)
            : tmplId
              ? odooUrl('product.template', tmplId)
              : null
          if (!odooHref) {
            return <span className="truncate">{label}</span>
          }
          return (
            <a
              href={odooHref}
              target="_blank"
              rel="noreferrer"
              aria-label={`Open ${label} in Odoo`}
              title="Open in Odoo"
              className="inline-flex items-center gap-1 truncate text-link hover:underline"
            >
              <span className="truncate">{label}</span>
              <ExternalLink size={10} aria-hidden="true" className="flex-shrink-0" />
            </a>
          )
        },
        // Only invoked for draft rows now — readonly predicate blocks
        // the click-to-edit path on existing rows.
        renderEditor: ({ value, onCommit, onCancel }) => (
          <ProductPicker
            value={toPickerValue(value as OdooM2O)}
            onChange={(next) => onCommit(next)}
            editorMode
            onCancel={onCancel}
          />
        ),
      },
      {
        key: 'customer_part_number',
        header: 'Part #',
        width: '12%',
        render: (v) => {
          const s = v as string | false
          return s ? <span >{s}</span> : <span className="text-tertiary">—</span>
        },
      },
      {
        key: 'packaging_id',
        header: 'Pack size',
        width: '10%',
        render: (v, row) => {
          const pack = v as OdooM2O | undefined
          if (!pack || !pack[0]) return <span className="text-tertiary">Any pack</span>
          const qty = !isDraft(row) ? row.pack_qty : undefined
          const uom = !isDraft(row) ? m2oName(row.pack_uom_id) : undefined
          return (
            <span className="truncate" title={pack[1]}>
              {pack[1]}
              {qty ? <span className="text-tertiary"> · {qty.toLocaleString()} {uom}</span> : null}
            </span>
          )
        },
        renderEditor: ({ value, row, onCommit, onCancel }) => (
          <PackPicker
            value={value as OdooM2O}
            productId={m2oId(row.product_id)}
            productTmplId={m2oId(row.product_tmpl_id)}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        ),
      },
      {
        key: 'min_quantity',
        header: 'Min qty',
        numeric: true,
        width: '8%',
        render: (v, row) => (
          <span>
            {formatQty(v as number | null)}
            {row.uom_name ? <span className="text-tertiary"> {row.uom_name}</span> : null}
          </span>
        ),
      },
      {
        key: 'fixed_price',
        header: 'Price',
        numeric: true,
        width: '10%',
        render: (v, row) => (
          <span>
            {formatPrice(v as number | false | null)}
            {row.uom_name ? <span className="text-tertiary"> / {row.uom_name}</span> : null}
          </span>
        ),
      },
      {
        key: 'date_start',
        header: 'Valid from',
        width: '10%',
        render: (v) => toDateOnly(v as string | false) || <span className="text-tertiary">—</span>,
        renderEditor: ({ value, onCommit, onCancel }) => (
          <DateEditor value={value as string | false} onCommit={onCommit} onCancel={onCancel} />
        ),
      },
      {
        key: 'date_end',
        header: 'Valid to',
        width: '10%',
        render: (v) => toDateOnly(v as string | false) || <span className="text-tertiary">—</span>,
        renderEditor: ({ value, onCommit, onCancel }) => (
          <DateEditor value={value as string | false} onCommit={onCommit} onCancel={onCancel} />
        ),
      },
      {
        key: 'last_paid_price',
        header: 'Our cost',
        numeric: true,
        readonly: true,
        width: '14%',
        sortFn: (a, b) =>
          _nullableNumberCompare(a.last_paid_price, b.last_paid_price),
        render: (_v, row) => formatLastPaid(row),
      },
      {
        key: '_upcoming_cost',
        header: 'Upcoming cost',
        numeric: true,
        readonly: true,
        width: '14%',
        sortFn: (a, b) =>
          _nullableNumberCompare(
            isDraft(a) ? null : (a.upcoming_cost_price ?? null),
            isDraft(b) ? null : (b.upcoming_cost_price ?? null),
          ),
        render: (_v, row) => renderUpcomingCost(row),
      },
      {
        key: '_margin_pct',
        header: 'Margin %',
        numeric: true,
        readonly: true,
        width: '8%',
        sortFn: (a, b) => _nullableNumberCompare(rowMarginPct(a), rowMarginPct(b)),
        render: (_v, row) => renderMargin(row),
      },
      {
        key: '_increase_pct',
        header: 'Increase %',
        numeric: true,
        sortable: false,
        width: '8%',
        // Transient action input — always renders blank. Type a number,
        // commit, and the cell PATCHes `fixed_price = price × (1+pct/100)`,
        // then clears.
        render: () => <span className="text-tertiary">—</span>,
        renderEditor: ({ value, onCommit, onCancel }) => (
          <IncreasePctEditor
            value={value as number | null | undefined}
            onCommit={onCommit}
            onCancel={onCancel}
          />
        ),
      },
    ],
    [],
  )

  // ─── commit plumbing ─────────────────────────────────────────────

  const colIdxFor = (key: string | number | symbol): number =>
    columns.findIndex((c) => c.key === key)

  /** Translate a UI-layer value (picker tuple / string / number) into the
   *  wire shape Odoo expects on PATCH/POST. */
  function toWireValue(
    key: keyof PricelistItem,
    value: unknown,
  ): unknown {
    if (
      key === 'product_id' ||
      key === 'manufacturer_id' ||
      key === 'country_of_origin_id' ||
      key === 'packaging_id'
    ) {
      return m2oId(value as OdooM2O | null | undefined)
    }
    if (key === 'min_quantity' || key === 'fixed_price') {
      return value === null || value === '' ? null : Number(value)
    }
    return value
  }

  function extractErrorMessage(err: unknown): string {
    if (err instanceof ApiError) return err.message.replace(/^[^:]+:\s*/, '')
    return String(err)
  }

  function routeMutationError(
    rowIdx: number,
    key: string | number | symbol,
    err: unknown,
  ): void {
    const colIdx = colIdxFor(key)
    if (err instanceof ApiError && err.status === 400) {
      setCellErrors((prev) => {
        const next = new Map(prev)
        next.set(cellKey(rowIdx, colIdx), extractErrorMessage(err))
        return next
      })
    } else {
      toast.error(`Save failed: ${extractErrorMessage(err)}`)
    }
  }

  function clearCellError(rowIdx: number, key: string | number | symbol): void {
    const k = cellKey(rowIdx, colIdxFor(key))
    setCellErrors((prev) => {
      if (!prev.has(k)) return prev
      const next = new Map(prev)
      next.delete(k)
      return next
    })
  }

  function applyPendingEdit(itemId: number, patch: Partial<PricelistItem>) {
    setPendingEdits((prev) => {
      const next = new Map(prev)
      const existing = next.get(itemId) ?? {}
      next.set(itemId, { ...existing, ...patch })
      return next
    })
  }

  function clearPendingEdit(itemId: number, key: keyof PricelistItem) {
    setPendingEdits((prev) => {
      const existing = prev.get(itemId)
      if (!existing || !(key in existing)) return prev
      const next = new Map(prev)
      const updated = { ...existing }
      delete updated[key]
      if (Object.keys(updated).length === 0) next.delete(itemId)
      else next.set(itemId, updated)
      return next
    })
  }

  // Fields that route through the confirm dialog before PATCHing. Edits
  // to anything else (part #, manufacturer, dates, etc.) save immediately
  // — they don't move money.
  function requiresPriceConfirm(field: keyof GridRow): boolean {
    return field === 'fixed_price' || field === 'min_quantity'
  }

  function rowLabelFor(row: GridRow): string {
    if (!isDraft(row)) {
      const product = row.product_id || row.product_tmpl_id
      if (product) return product[1] ?? `item #${row.id}`
      if (row.customer_part_number) return row.customer_part_number
      return `item #${row.id}`
    }
    return 'new item'
  }

  // Single PATCH-firing path used by both the immediate-commit branches
  // (non-pricing fields) and the confirm-dialog "Confirm" handler.
  function firePatch(
    args: {
      itemId: number
      rowIdx: number
      field: keyof PricelistItem
      wireValue: unknown
      // For pricing fields we surface an undo toast after success. Caller
      // supplies the old value + display label so the toast can reference
      // the row that just changed.
      undo?: {
        oldValue: number | null
        newValue: number
        rowLabel: string
        // 'increase' routes errors back to the Increase % cell; otherwise
        // routes back to the original cell key.
        errorRouteKey: keyof GridRow
      }
    },
  ): void {
    const { itemId, rowIdx, field, wireValue, undo } = args
    patchItem.mutate(
      {
        itemId,
        pricelistId: pricelist.id,
        patch: { [field]: wireValue } as ItemPatch,
      },
      {
        onSuccess: () => {
          clearPendingEdit(itemId, field)
          if (undo) {
            showUndoToast(itemId, field, undo.oldValue, undo.newValue, undo.rowLabel)
          }
        },
        onError: (err) => {
          clearPendingEdit(itemId, field)
          routeMutationError(rowIdx, undo?.errorRouteKey ?? field, err)
        },
      },
    )
  }

  // Sonner toast with a 10s Undo affordance. Clicking Undo PATCHes the
  // prior value back; the restore confirmation toast does NOT chain
  // another undo (no infinite-loop foot-gun).
  function showUndoToast(
    itemId: number,
    field: keyof PricelistItem,
    oldValue: number | null,
    newValue: number,
    rowLabel: string,
  ): void {
    const fmt =
      field === 'fixed_price' ? formatPriceValue : (n: number | null) => (n ?? '—').toString()
    toast.success(
      `${rowLabel}: ${PRICE_FIELD_LABELS[field as 'fixed_price' | 'min_quantity']} ${fmt(oldValue)} → ${fmt(newValue)}`,
      {
        duration: 10_000,
        action: {
          label: 'Undo',
          onClick: () => undoPriceChange(itemId, field, oldValue, newValue, rowLabel),
        },
      },
    )
  }

  function undoPriceChange(
    itemId: number,
    field: keyof PricelistItem,
    oldValue: number | null,
    _newValue: number,
    rowLabel: string,
  ): void {
    // Optimistically restore the prior value in the grid.
    applyPendingEdit(itemId, { [field]: oldValue } as Partial<PricelistItem>)
    patchItem.mutate(
      {
        itemId,
        pricelistId: pricelist.id,
        patch: { [field]: oldValue } as ItemPatch,
      },
      {
        onSuccess: () => {
          clearPendingEdit(itemId, field)
          toast.success(
            `${rowLabel}: ${PRICE_FIELD_LABELS[field as 'fixed_price' | 'min_quantity']} restored`,
            { duration: 3_000 },
          )
        },
        onError: (err) => {
          clearPendingEdit(itemId, field)
          toast.error(`Undo failed: ${extractErrorMessage(err)}`)
        },
      },
    )
  }

  const handleCellChange = (
    rowIdx: number,
    key: keyof GridRow,
    value: unknown,
  ): void => {
    const row = rows[rowIdx]
    if (!row) return
    clearCellError(rowIdx, key)

    // Increase % is a transient action — it never writes its own field.
    // Instead it recalculates `fixed_price` and PATCHes that.
    if (key === '_increase_pct') {
      const pct = Number(value)
      if (!Number.isFinite(pct)) return
      const currentPrice = Number(row.fixed_price)
      if (!Number.isFinite(currentPrice)) {
        toast.error('Cannot apply increase: row has no current price.')
        return
      }
      // Round to 4 decimal places — Odoo's price precision.
      const newPrice = Math.round(currentPrice * (1 + pct / 100) * 10000) / 10000

      if (isDraft(row)) {
        commitDraftCell(
          row,
          'fixed_price' as keyof DraftRow,
          newPrice,
          rowIdx,
        )
        return
      }

      // Hold for confirm. Optimistic update lets the grid paint the new
      // price while the dialog is open; cancel rolls it back.
      applyPendingEdit(row.id, { fixed_price: newPrice } as Partial<PricelistItem>)
      setPendingConfirm({
        itemId: row.id,
        rowIdx,
        field: 'fixed_price',
        oldValue: typeof currentPrice === 'number' ? currentPrice : null,
        newValue: newPrice,
        rowLabel: rowLabelFor(row),
        source: 'increase',
        increasePct: pct,
      })
      return
    }

    if (isDraft(row)) {
      commitDraftCell(row, key as keyof DraftRow, value, rowIdx)
      return
    }

    // Existing row — PATCH the single field.
    const itemKey = key as keyof PricelistItem
    const wireValue = toWireValue(itemKey, value)

    // Optimistically layer the new value so the grid paints it immediately.
    applyPendingEdit(row.id, { [itemKey]: value } as Partial<PricelistItem>)

    // Pricing fields hold for confirm; everything else PATCHes immediately.
    if (requiresPriceConfirm(itemKey as keyof GridRow)) {
      const oldValue = typeof row[itemKey] === 'number'
        ? (row[itemKey] as number)
        : null
      const newValueNum = typeof wireValue === 'number' ? wireValue : Number(wireValue)
      setPendingConfirm({
        itemId: row.id,
        rowIdx,
        field: itemKey as 'fixed_price' | 'min_quantity',
        oldValue,
        newValue: newValueNum,
        rowLabel: rowLabelFor(row),
        source: 'cell',
      })
      return
    }

    firePatch({
      itemId: row.id,
      rowIdx,
      field: itemKey,
      wireValue,
    })
  }

  // ─── confirm dialog handlers ─────────────────────────────────────

  function handleConfirmAccept() {
    if (!pendingConfirm) return
    const { itemId, rowIdx, field, oldValue, newValue, rowLabel, source } = pendingConfirm
    setPendingConfirm(null)
    firePatch({
      itemId,
      rowIdx,
      field,
      wireValue: newValue,
      undo: {
        oldValue,
        newValue,
        rowLabel,
        errorRouteKey: source === 'increase' ? '_increase_pct' : field,
      },
    })
  }

  function handleConfirmCancel() {
    if (!pendingConfirm) return
    // Roll the optimistic update back so the cell returns to its
    // pre-edit display value.
    clearPendingEdit(pendingConfirm.itemId, pendingConfirm.field)
    setPendingConfirm(null)
  }

  // ─── draft rows ──────────────────────────────────────────────────

  const handleAddRow = () => {
    setDraftRows((prev) => [...prev, makeDraftRow()])
  }

  /** New row for the same product, pack and part # — the operator sets the
   *  quantity (and price). Min qty starts blank-ish at 1 like any new row. */
  const handleAddTier = (row: GridRow) => {
    const draft = makeDraftRow({
      product_id: row.product_id || false,
      product_tmpl_id: row.product_tmpl_id || false,
      packaging_id: row.packaging_id || false,
      customer_part_number: row.customer_part_number || false,
      uom_name: row.uom_name,
      min_quantity: 0,
    })
    setDraftRows((prev) => [...prev, draft])
  }

  function commitDraftCell(
    row: DraftRow,
    key: keyof DraftRow,
    value: unknown,
    rowIdx: number,
  ) {
    const next: DraftRow = { ...row, [key]: value } as DraftRow
    // A pack belongs to one product — changing the product clears it.
    if (key === 'product_id') next.packaging_id = false
    setDraftRows((prev) => prev.map((r) => (r.id === row.id ? next : r)))

    // POST as soon as the row has the minimum required fields.
    const productId = m2oId(next.product_id)
    const tmplId = m2oId(next.product_tmpl_id)
    const price = next.fixed_price
    const hasPrice = typeof price === 'number' && !Number.isNaN(price)
    if ((productId !== null || tmplId !== null) && hasPrice) {
      createItem.mutate(
        {
          pricelistId: pricelist.id,
          item: {
            ...(productId !== null ? { product_id: productId } : { product_tmpl_id: tmplId! }),
            packaging_id: m2oId(next.packaging_id),
            fixed_price: price,
            min_quantity: next.min_quantity,
            date_start: toWireDate(next.date_start),
            date_end: toWireDate(next.date_end),
            customer_part_number: toWireString(next.customer_part_number),
            manufacturer_trade_name: toWireString(next.manufacturer_trade_name),
            manufacturer_id: m2oId(next.manufacturer_id),
            country_of_origin_id: m2oId(next.country_of_origin_id),
          },
        },
        {
          onSuccess: () => {
            // Refetch carries the real row — drop the draft.
            setDraftRows((prev) => prev.filter((r) => r.id !== row.id))
          },
          onError: (err) => {
            routeMutationError(rowIdx, key, err)
          },
        },
      )
    }
  }

  const handleDeleteRow = (row: GridRow) => {
    if (isDraft(row)) {
      setDraftRows((prev) => prev.filter((r) => r.id !== row.id))
      return
    }
    if (
      !window.confirm(
        `Delete item ${row.product_id ? row.product_id[1] : row.id}?`,
      )
    ) {
      return
    }
    deleteItem.mutate(
      { itemId: row.id, pricelistId: pricelist.id },
      {
        onError: (err) => toast.error(`Delete failed: ${extractErrorMessage(err)}`),
      },
    )
  }

  // ─── dirty / meta ────────────────────────────────────────────────

  const dirtyCells = useMemo(() => {
    const set = new Set<string>()
    for (const [id, patch] of pendingEdits) {
      const rowIdx = rows.findIndex((r) => !isDraft(r) && r.id === id)
      if (rowIdx === -1) continue
      for (const k of Object.keys(patch) as Array<keyof PricelistItem>) {
        set.add(cellKey(rowIdx, colIdxFor(k as keyof GridRow)))
      }
    }
    return set
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEdits, rows])

  const validators = useMemo(
    () => ({
      fixed_price: (v: unknown) => {
        if (v === null || v === '' || v === undefined) return null
        const n = Number(v)
        if (Number.isNaN(n)) return 'Must be a number'
        return n >= 0 ? null : 'Must be ≥ 0'
      },
      min_quantity: (v: unknown) => {
        if (v === null || v === '' || v === undefined) return null
        const n = Number(v)
        if (Number.isNaN(n)) return 'Must be a number'
        return n >= 0 ? null : 'Must be ≥ 0'
      },
    }),
     
    [],
  )

  return (
    <div className="flex flex-col gap-3">
      {upcomingCount > 0 ? (
        <label className="flex items-center gap-2 text-sm text-secondary">
          <input
            type="checkbox"
            checked={onlyUpcoming}
            onChange={(e) => setOnlyUpcoming(e.target.checked)}
          />
          Only rows with an upcoming vendor cost change ({upcomingCount})
        </label>
      ) : null}
      <EditableGrid<GridRow>
        rows={rows}
        columns={columns}
        onCellChange={handleCellChange}
        onAddRow={handleAddRow}
        addRowLabel="Add item"
        dirtyCells={dirtyCells}
        cellErrors={cellErrors}
        validators={validators}
        moduleId={MODULE_ID}
        // 14 columns — bump the min-width so all are readable before
        // horizontal scroll engages.
        minTableWidth="1740px"
        rowActions={(row) => (
          <span className="inline-flex items-center gap-1">
          {!isDraft(row) ? (
            <button
              type="button"
              aria-label={`Add a tier or pack for ${m2oName(row.product_id) || m2oName(row.product_tmpl_id) || `item ${row.id}`}`}
              title="Add tier / pack size"
              onClick={() => handleAddTier(row)}
              className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs text-tertiary hover:bg-surface-hover hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-border-default"
            >
              <Plus size={14} />
            </button>
          ) : null}
          <button
            type="button"
            aria-label={
              isDraft(row)
                ? 'Discard draft row'
                : `Delete ${row.product_id ? row.product_id[1] : `item ${row.id}`}`
            }
            onClick={() => handleDeleteRow(row)}
            className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded-xs text-tertiary hover:bg-danger-bg hover:text-danger-text focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-danger"
          >
            <Trash2 size={14} />
          </button>
          </span>
        )}
        title={
          <span>
            {pricelist.name}{' '}
            <span className="text-role-metadata">
              · {rows.length} item{rows.length === 1 ? '' : 's'}
              {dirtyCells.size > 0 ? ` · ${dirtyCells.size} saving` : ''}
            </span>
          </span>
        }
        meta={
          <span className="inline-flex items-center gap-2">
            {fetching ? <span>Loading…</span> : null}
            <button
              type="button"
              onClick={refetch}
              className="inline-flex cursor-pointer items-center rounded-xs border border-border-default bg-surface px-2 py-0.5 text-2xs uppercase tracking-widest text-secondary hover:bg-surface-hover"
            >
              Refresh
            </button>
          </span>
        }
        hint={
          <span>
            Tab | Enter | Esc | ↑↓ | Edits save immediately. Click a header
            to sort; drag a header to reorder. Non-fixed rules (%, formula)
            shown read-only below.
          </span>
        }
      />

      {nonFixedItems.length > 0 ? (
        <NonFixedItems items={nonFixedItems} />
      ) : null}

      <PriceChangeConfirmDialog
        pending={pendingConfirm}
        onConfirm={handleConfirmAccept}
        onCancel={handleConfirmCancel}
      />
    </div>
  )
}

function PriceChangeConfirmDialog({
  pending,
  onConfirm,
  onCancel,
}: {
  pending: PendingConfirm | null
  onConfirm: () => void
  onCancel: () => void
}) {
  // Modal owns its open state via the `open` prop; we map null/non-null
  // to closed/open.
  const open = pending !== null
  const fmt = (v: number | null) =>
    pending?.field === 'fixed_price' ? formatPriceValue(v) : (v ?? '—').toString()
  const fieldLabel = pending ? PRICE_FIELD_LABELS[pending.field] : ''
  return (
    <Modal
      open={open}
      onOpenChange={(o) => {
        if (!o) onCancel()
      }}
      tone="warning"
      title={pending?.source === 'increase' ? 'Apply price increase?' : `Confirm ${fieldLabel.toLowerCase()} change`}
      description={
        pending ? (
          <span>
            <strong className="text-primary">{pending.rowLabel}</strong>
            {pending.source === 'increase' ? (
              <> — applying a {pending.increasePct?.toLocaleString(undefined, { maximumFractionDigits: 2 })}% increase.</>
            ) : null}
          </span>
        ) : null
      }
      footer={
        <>
          <Button variant="secondary" size="sm" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" size="sm" onClick={onConfirm}>
            Confirm change
          </Button>
        </>
      }
    >
      {pending ? (
        <div className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 py-2 text-sm">
          <span className="text-tertiary">Field</span>
          <span className="text-primary">{fieldLabel}</span>
          <span className="text-tertiary">From</span>
          <span className="text-primary">{fmt(pending.oldValue)}</span>
          <span className="text-tertiary">To</span>
          <span className="font-semibold text-primary">{fmt(pending.newValue)}</span>
        </div>
      ) : null}
    </Modal>
  )
}

// ─── helpers ──────────────────────────────────────────────────────

function makeDraftRow(overrides: Partial<DraftRow> = {}): DraftRow {
  return {
    id: -Date.now(),
    _draft: true,
    compute_price: 'fixed',
    applied_on: '1_product',
    product_id: false,
    fixed_price: false,
    min_quantity: 1,
    date_start: false,
    date_end: false,
    customer_part_number: false,
    manufacturer_trade_name: false,
    manufacturer_id: false,
    country_of_origin_id: false,
    packaging_id: false,
    pricelist_id: false,
    product_tmpl_id: false,
    currency_id: false,
    manufacturer_name: false,
    country_of_origin: false,
    last_paid_price: null,
    last_paid_uom_name: null,
    last_paid_at: null,
    last_paid_packaging_name: null,
    last_paid_packaging_qty: null,
    approved_vendor_ids: [],
    approved_vendors: [],
    ...overrides,
  }
}

function toWireDate(v: string | false): string | null {
  if (!v) return null
  return v
}

/** Date part of an Odoo date/datetime string ('' when unset). */
function toDateOnly(v: string | false | null | undefined): string {
  return v ? v.slice(0, 10) : ''
}

function toWireString(v: string | false): string | null {
  if (!v) return null
  return v
}

function formatQty(q: number | null | undefined): ReactNode {
  if (q === null || q === undefined) return '—'
  return q.toLocaleString()
}

function formatPrice(p: number | false | null | undefined): ReactNode {
  if (p === null || p === undefined || p === false) return <span className="text-tertiary">—</span>
  return p.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  })
}

/** Plain-string variant of formatPrice for use in toast / dialog bodies
 *  where a ReactNode isn't a fit. Renders '—' for null. */
function formatPriceValue(p: number | null | undefined): string {
  if (p === null || p === undefined) return '—'
  return p.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  })
}

function formatLastPaid(row: GridRow): ReactNode {
  const price = row.last_paid_price
  if (price == null) return <span className="text-tertiary">—</span>
  const formatted = price.toLocaleString(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 4,
  })
  const uom = row.last_paid_uom_name ? ` / ${row.last_paid_uom_name}` : ''
  const packaging = row.last_paid_packaging_name
    ? ` · ${row.last_paid_packaging_name}`
    : ''
  const at = row.last_paid_at ?? undefined
  return (
    <span title={at ?? undefined}>
      {formatted}
      <span className="text-tertiary">{uom}{packaging}</span>
    </span>
  )
}

/**
 * Margin% relative to the last paid cost. Mirrors the Quotes module
 * helper. Returns null when either side is missing or non-positive.
 */
function rowMarginPct(row: GridRow): number | null {
  const price = Number(row.fixed_price)
  const cost = row.last_paid_price
  if (!Number.isFinite(price) || price <= 0) return null
  if (cost == null || !Number.isFinite(cost) || cost <= 0) return null
  return ((price - cost) / price) * 100
}

function renderUpcomingCost(row: GridRow): ReactNode {
  if (isDraft(row) || !hasUpcomingCost(row)) return <span className="text-tertiary">—</span>
  const price = row.upcoming_cost_price as number
  const uom = row.upcoming_cost_uom_name ? ` / ${row.upcoming_cost_uom_name}` : ''
  const pct = row.upcoming_cost_pct_change
  const tiers = (row.upcoming_cost_tiers ?? [])
    .map((t) => `MOQ ${t.min_qty.toLocaleString()}: ${formatPriceValue(t.price)}${uom}`)
    .join('\n')
  const title =
    `${row.upcoming_cost_vendor_name ?? 'Vendor'} price takes effect ${formatShortDate(row.upcoming_cost_effective_date as string)}.` +
    ` Odoo keeps today's cost until then.${tiers ? `\n${tiers}` : ''}`
  return (
    <span title={title} className="flex flex-col items-end gap-0.5">
      <span>
        {formatPriceValue(price)}
        <span className="text-tertiary">{uom}</span>
      </span>
      <Chip size="xs" variant="warning">
        From {formatShortDate(row.upcoming_cost_effective_date as string)}
        {row.upcoming_cost_vendor_name ? ` · ${row.upcoming_cost_vendor_name}` : ''}
        {pct != null ? ` · ${pct > 0 ? '+' : ''}${pct.toFixed(1)}%` : ''}
      </Chip>
    </span>
  )
}

function renderMargin(row: GridRow): ReactNode {
  const m = rowMarginPct(row)
  if (m == null) return <span className="text-tertiary">—</span>
  const after = isDraft(row) ? null : upcomingMarginPct(row)
  if (after != null) {
    const worse = after < m
    return (
      <span title="Margin now → margin at the scheduled vendor cost">
        {m.toFixed(1)}% →{' '}
        <Chip size="xs" variant={after < 0 ? 'danger' : worse ? 'warning' : 'success'}>
          {after.toFixed(1)}%
        </Chip>
      </span>
    )
  }
  const text = `${m.toFixed(1)}%`
  if (m < 0) {
    return (
      <Chip size="xs" variant="danger">
        {text}
      </Chip>
    )
  }
  return <span>{text}</span>
}

function _nullableNumberCompare(
  a: number | null | undefined,
  b: number | null | undefined,
): number {
  if (a == null && b == null) return 0
  if (a == null) return 1
  if (b == null) return -1
  return a - b
}

// ─── sub-components ──────────────────────────────────────────────

function PackPicker({
  value,
  productId,
  productTmplId,
  onCommit,
  onCancel,
}: {
  value: OdooM2O
  productId: number | null
  productTmplId: number | null
  onCommit: (next: unknown) => void
  onCancel: () => void
}) {
  const { data, isLoading } = usePackagings({ productId, productTmplId })
  const options = data?.packagings ?? []
  const current = value && value[0] ? String(value[0]) : ''
  const commit = (raw: string) => {
    if (raw === '') return onCommit(false)
    const opt = options.find((o) => String(o.id) === raw)
    onCommit(opt ? ([opt.id, opt.name] as OdooM2O) : false)
  }
  if (!productId && !productTmplId) {
    return <span className="px-[var(--cell-padding-x)] text-tertiary">Pick a product first</span>
  }
  return (
    <select
      autoFocus
      value={current}
      disabled={isLoading}
      onChange={(e) => commit(e.target.value)}
      onBlur={(e) => commit(e.target.value)}
      onKeyDown={(e) => {
        if (e.key === 'Escape') {
          e.preventDefault()
          onCancel()
        }
      }}
      className="h-full w-full border-none bg-transparent px-[var(--cell-padding-x)] text-sm text-primary outline-none"
    >
      <option value="">Any pack{data?.uom_name ? ` (per ${data.uom_name})` : ''}</option>
      {options.map((o) => (
        <option key={o.id} value={o.id}>
          {o.name} · {o.qty.toLocaleString()} {o.uom_name ?? ''}
        </option>
      ))}
    </select>
  )
}

function DateEditor({
  value,
  onCommit,
  onCancel,
}: {
  value: string | false
  onCommit: (next: unknown) => void
  onCancel: () => void
}) {
  // Odoo sends pricelist item dates as datetimes ("2026-10-08 00:00:00").
  // <input type="date"> only accepts YYYY-MM-DD and renders anything else
  // blank, so the editor must seed with the date part or it shows an empty
  // box that still holds (and re-saves) the old value.
  const original = toDateOnly(value)
  const [draft, setDraft] = useState<string>(original)
  function commit() {
    if (draft === original) {
      onCancel()
      return
    }
    onCommit(draft === '' ? null : draft)
  }
  return (
    <div className="flex h-full w-full items-center">
      <input
        type="date"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault()
            commit()
          } else if (e.key === 'Escape') {
            e.preventDefault()
            onCancel()
          }
        }}
        className="h-full min-w-0 flex-1 border-none bg-transparent px-[var(--cell-padding-x)] text-sm text-primary outline-none"
      />
      {original ? (
        <Button
          variant="ghost"
          size="sm"
          iconOnly
          aria-label="Clear date"
          title="Clear date"
          // Keep focus on the input so its blur doesn't commit first.
          onMouseDown={(e) => e.preventDefault()}
          onClick={() => onCommit(null)}
        >
          <X size={14} />
        </Button>
      ) : null}
    </div>
  )
}

function IncreasePctEditor({
  value,
  onCommit,
  onCancel,
}: {
  value: number | null | undefined
  onCommit: (next: unknown) => void
  onCancel: () => void
}) {
  // The cell never persists this value — render the editor blank by
  // default so each invocation is a fresh action input.
  const [draft, setDraft] = useState<string>(
    value == null ? '' : String(value),
  )
  const [error, setError] = useState<string | null>(null)

  function tryCommit() {
    const trimmed = draft.trim()
    if (trimmed === '') {
      onCancel()
      return
    }
    const n = Number(trimmed)
    if (!Number.isFinite(n)) {
      setError('Must be a number')
      return
    }
    if (n <= 0 || n >= 1000) {
      setError('Must be > 0 and < 1000')
      return
    }
    setError(null)
    onCommit(n)
  }

  return (
    <input
      type="number"
      step="any"
      placeholder="%"
      autoFocus
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={tryCommit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault()
          tryCommit()
        } else if (e.key === 'Escape') {
          e.preventDefault()
          onCancel()
        }
      }}
      aria-invalid={Boolean(error)}
      className="h-full w-full border-none bg-transparent px-[var(--cell-padding-x)] text-right text-sm tabular-nums text-primary outline-none"
    />
  )
}

function NonFixedItems({ items }: { items: PricelistItem[] }) {
  return (
    <section className="rounded-sm border border-border-subtle bg-surface-sunk p-3">
      <h3 className="mb-2 text-role-section-label">
        Non-fixed rules · read-only
      </h3>
      <p className="mb-3 text-xs text-secondary">
        Percentage- and formula-based rules aren't editable here. Adjust them
        via the Odoo pricelist form.
      </p>
      <table className="w-full border-collapse text-sm">
        <thead className="border-b border-border-subtle">
          <tr className="text-left">
            <th className="py-1.5 pr-3 text-role-section-label">
              Product
            </th>
            <th className="py-1.5 pr-3 text-role-section-label">
              Rule
            </th>
            <th className="py-1.5 pr-3 text-role-section-label">
              Open
            </th>
          </tr>
        </thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.id} className="border-b border-border-subtle last:border-b-0">
              <td className="py-1.5 pr-3 text-primary">
                {it.product_tmpl_id
                  ? it.product_tmpl_id[1]
                  : it.product_id
                    ? it.product_id[1]
                    : `Item ${it.id}`}
              </td>
              <td className="py-1.5 pr-3">
                <Chip size="xs" variant="warning">
                  {it.compute_price}
                </Chip>
              </td>
              <td className="py-1.5 pr-3">
                <a
                  href={odooUrl('product.pricelist.item', it.id)}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 text-xs text-link hover:underline"
                >
                  Odoo <ExternalLink size={10} />
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  )
}
